import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import SEO from '../components/seo/SEO';
import { resetPassword } from '../services/authService';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 3rem;
  }
`;

const Card = styled.div`
  max-width: 26rem;
  margin: 0 auto;
  padding: 2rem;
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.card};
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.75rem;
  font-weight: 700;
`;

const Sub = styled.p`
  margin: 0 0 1.5rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: 0.9375rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Input = styled.input`
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: 0.9375rem;
`;

const ErrorText = styled.p`
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.colors.destructive || '#dc2626'};
  font-size: 0.875rem;
`;

const SuccessText = styled.p`
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.9375rem;
  line-height: 1.6;
`;

const FooterText = styled.p`
  margin: 1.25rem 0 0;
  text-align: center;
  font-size: 0.875rem;

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    if (!token) {
      setError(t('auth.resetMissingToken'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await resetPassword({ token, password });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.message || t('auth.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!token && !done) {
    return (
      <PageWrapper>
        <SEO title={t('auth.resetTitle')} robots="noindex, follow" />
        <Header />
        <Main>
          <Card>
            <Title>{t('auth.resetTitle')}</Title>
            <ErrorText>{t('auth.resetMissingToken')}</ErrorText>
            <FooterText>
              <Link to="/forgot-password">{t('auth.forgotTitle')}</Link>
            </FooterText>
          </Card>
        </Main>
        <Footer />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <SEO title={t('auth.resetTitle')} robots="noindex, follow" />
      <Header />
      <Main>
        <Card>
          <Title>{t('auth.resetTitle')}</Title>
          <Sub>{done ? t('auth.resetSuccess') : t('auth.resetSub')}</Sub>
          {!done ? (
            <form onSubmit={handleSubmit}>
              {error ? <ErrorText>{error}</ErrorText> : null}
              <Field>
                {t('auth.newPasswordLabel')}
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </Field>
              <Field>
                {t('auth.confirmPasswordLabel')}
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </Field>
              <Button type="submit" disabled={loading} style={{ width: '100%' }}>
                {loading ? t('auth.resetting') : t('auth.resetSubmit')}
              </Button>
            </form>
          ) : (
            <>
              <SuccessText>{t('auth.resetSuccess')}</SuccessText>
              <Button type="button" onClick={() => navigate('/login')} style={{ width: '100%' }}>
                {t('auth.signIn')}
              </Button>
            </>
          )}
        </Card>
      </Main>
      <Footer />
    </PageWrapper>
  );
}
